import { DefinedInstallationType, INSTALLATION_TYPE } from 'app/shared/models/location-details';
import { findFieldById, findSectionById, normalizeSeriesName } from '../base-utils';
import {
    BasicInfoCommonFieldIds,
    ChannelInstallationFieldIds,
    FlumeInstallationFieldIds,
    PipeInstallationFieldIds,
    TAssembledApiData,
    WeirInstallationFieldIds,
} from '../common.models';
import { INSTALLATION_DEFAULT_CONSTANTS } from '../generic/installation-types/config-models';
import { AutocompleteField, FieldType, SelectField, TextField } from '../../models/field-models';
import { RainGaugeLocation } from '../../../models/location-card.model';
import { UnknownLocationPayload } from './base-api-models';
import { UnknownLocationCardState } from './models';
import { UnknownSectionIds } from './base-config-models';

export function gatherUnknownBasicInfoPayload(state: UnknownLocationCardState): UnknownLocationPayload[] {
    const result: UnknownLocationPayload[] = [];

    let mp1Info: Partial<UnknownLocationPayload> = {};
    Object.keys(SaveBasicInfoPayload).forEach((key) => {
        mp1Info[key] = SaveBasicInfoPayload[key](state);
    });

    const installationType = SaveInstallationPayload(state);
    mp1Info = { ...mp1Info, ...installationType };

    result.push(mp1Info as UnknownLocationPayload);

    return result;
}

export const SaveBasicInfoPayload: { [key: string]: (state: UnknownLocationCardState) => TAssembledApiData } = {
    locationName: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.LocationName).value,
    ipaddress: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.IpAddress).value,
    assignedRainGaugeId: (state) => {
        const rainGaugeField = findFieldById(state.formState, BasicInfoCommonFieldIds.AssignedRainGauge) as SelectField<
            RainGaugeLocation,
            string
        >;
        const rainGauge = rainGaugeField.value;

        if (!rainGauge) {
            return null;
        }

        const assignedRainGauge = state.options.rainGauges.data.find((rg) => rg.locationName === rainGauge);

        if (!assignedRainGauge) {
            return null;
        }

        return assignedRainGauge.locationId;
    },
    communicationType: (state) =>
        Number(findFieldById(state.formState, BasicInfoCommonFieldIds.CommunicationType).value),
    description: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Description).value,
    installationDate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.InstallationDate).value,
    latitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Latitude).value,
    longitude: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Longitude).value,
    manholeaddress: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ManholeAddress).value,
    manholedepth: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ManholeDepth).value,
    qFinalEntityID: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.QFinalEntityID).value,
    serialnumber: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SerialNumber).value,
    series: (state) => normalizeSeriesName(findFieldById(state.formState, BasicInfoCommonFieldIds.Series).value as string),
    status: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.Status).value,
    maintenanceInterval: (state) =>
        findFieldById(state.formState, BasicInfoCommonFieldIds.MaintenanceInterval)?.value ?? null,
    apnProvider: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.ApnProvider).value,
    samplerate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SampleRate).value,
    fastrate: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.FastRate).value,
    usedaylightsaving: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.UseDaylightSaving).value,
    synctime: (state) => findFieldById(state.formState, BasicInfoCommonFieldIds.SyncTime).value,
    customProperties: (state) => {
        const customPropertiesSection = findSectionById(
            state.formState,
            BasicInfoCommonFieldIds.CustomPropertiesSection,
        );
        const customProperties: { key: string; value: string }[] = [];

        let i = 0;
        while (true) {
            if (customPropertiesSection!.fields[`${BasicInfoCommonFieldIds.CustomPropertyKey}-${i}`] === undefined) {
                break;
            }

            const keyField = customPropertiesSection!.fields[
                `${BasicInfoCommonFieldIds.CustomPropertyKey}-${i}`
            ] as AutocompleteField<string, string>;
            const valueField = customPropertiesSection!.fields[
                `${BasicInfoCommonFieldIds.CustomPropertyValue}-${i}`
            ] as TextField;

            customProperties.push({
                key: keyField.value,
                value: valueField.value,
            });

            i++;
        }

        return customProperties;
    },
};

export const SaveInstallationPayload = (state: UnknownLocationCardState) => {
    const basicInfo = state.formState.config.sections.find((s) => s.id === UnknownSectionIds.BasicInfo);

    const installationType = basicInfo?.fields[BasicInfoCommonFieldIds.InstallationType].value as string;

    if (!installationType) {
        console.error('Installation type not found');
        return null;
    }

    const [type, shape] = installationType.split(':').map((s) => s.trim());

    if (type === INSTALLATION_TYPE.PIPE) {
        const FieldIds = PipeInstallationFieldIds;
        return {
            pipe: {
                height: findFieldById(state.formState, FieldIds.Height).value,
                width: findFieldById(state.formState, FieldIds.Width).value,
                lookupDesc: type,
                hc: findFieldById(state.formState, FieldIds.Hc).value,
                roughness: findFieldById(state.formState, FieldIds.Roughness).value,
                effectiveRoughness: findFieldById(state.formState, FieldIds.EffectiveRoughness).value,
                slope: findFieldById(state.formState, FieldIds.Slope).value,
                frictionFactor: findFieldById(state.formState, FieldIds.FrictionFactor).value,
                nominalDiameter: findFieldById(state.formState, FieldIds.NominalDiameter).value,
                startDate: findFieldById(state.formState, FieldIds.StartDate).value,
                installationType: type,
                MonitoringPoint: 1,
                installationID: DefinedInstallationType.Pipe,
                shapeType: shape,
                installationShapeType: shape,
                depthSensor: 100,
                velocitySensor: 100,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Pipe,
            installationtypeid: DefinedInstallationType.Pipe,
            installationShapeType: shape,
        };
    }

    if (type === INSTALLATION_TYPE.WEIR) {
        const FieldIds = WeirInstallationFieldIds;

        return {
            weir: {
                range: findFieldById(state.formState, FieldIds.Range).value,
                capacity: findFieldById(state.formState, FieldIds.Capacity).value,
                height: findFieldById(state.formState, FieldIds.Height).value,
                coefficient: findFieldById(state.formState, FieldIds.Coefficient).value,
                length: findFieldById(state.formState, FieldIds.Length).value,
                breadth: findFieldById(state.formState, FieldIds.Breadth).value,
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: 1,
                installationID: DefinedInstallationType.Weir,
                shapeType: shape,
                installationShapeType: shape,
                ...INSTALLATION_DEFAULT_CONSTANTS,
                depthSensor: 100,
                velocitySensor: 100,
            },
            installationID: DefinedInstallationType.Weir,
            installationtypeid: DefinedInstallationType.Weir,
            installationShapeType: shape,
        };
    }

    if (type === INSTALLATION_TYPE.FLUME) {
        const FieldIds = FlumeInstallationFieldIds;

        return {
            flume: {
                range: findFieldById(state.formState, FieldIds.Range).value,
                capacity: findFieldById(state.formState, FieldIds.Capacity).value,
                width: findFieldById(state.formState, FieldIds.Width).value,
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: 1,
                installationID: DefinedInstallationType.Flume,
                shapeType: shape,
                installationShapeType: shape,
                depthSensor: 100,
                velocitySensor: 100,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Flume,
            installationtypeid: DefinedInstallationType.Flume,
            installationShapeType: shape,
        };
    }

    if (type === INSTALLATION_TYPE.CHANNEL) {
        const FieldIds = ChannelInstallationFieldIds;

        const entriesSection = findSectionById(state.formState, FieldIds.ArraySection);
        const numberOfEntriesField = findFieldById(state.formState, FieldIds.NumberOfEntries, FieldType.Number);
        const rangeField = findFieldById(state.formState, FieldIds.Range, FieldType.Number);

        if (!entriesSection || !numberOfEntriesField || !rangeField) {
            console.error('Channel installation fields not found');
            return null;
        }

        const numberOfEntries = numberOfEntriesField.value;
        const entries: { depth: number; quantity: number }[] = [];

        for (let i = 0; i < Number(numberOfEntries); i++) {
            const depthField = entriesSection.fields[`${FieldIds.Depth}-${i}`];
            const quantityField = entriesSection.fields[`${FieldIds.Quantity}-${i}`];

            if (depthField?.type !== FieldType.Number || quantityField?.type !== FieldType.Number) {
                console.error('Channel installation entry fields not found');
                return null;
            }

            entries.push({
                depth: depthField.value,
                quantity: quantityField.value,
            });
        }

        return {
            channel: {
                range: rangeField.value,
                numberOfEntries: numberOfEntries,
                entries: entries,
                lookupDesc: type,
                installationType: type,
                MonitoringPoint: 1,
                installationID: DefinedInstallationType.Channel,
                shapeType: shape,
                installationShapeType: shape,
                depthSensor: 100,
                velocitySensor: 100,
                ...INSTALLATION_DEFAULT_CONSTANTS,
            },
            installationID: DefinedInstallationType.Channel,
            installationtypeid: DefinedInstallationType.Channel,
            installationShapeType: shape,
        };
    }

    console.error('Installation type not supported');
    return null;
};
